/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "@/lib/utils";
import { useActionLoggerOptional } from "@/services/logger/logger-hooks";
import {
  buildButtonActionTitle,
  loggerContextManager,
} from "@/services/logger/smart-context-provider";
import {
  LogCategories,
  LogEventTypeEnum,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { capitalizeInitialLetter } from "@/lib/string-util";

function getNodeText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join(" ").trim();
  }
  if (React.isValidElement(node)) {
    return getNodeText(node.props.children);
  }
  return "";
}

export function normalizeTitle(text: string) {
  return capitalizeInitialLetter(
    text.trim().replace(/\s+/g, " ").toLowerCase(),
  );
}

function getDialogOpenedDurationMs(dialogName: string): number {
  const ctx = loggerContextManager.getCurrentDialogContext();
  if (ctx?.dialogName !== dialogName) return 0;
  return ctx?.openedAtMs != null ? Date.now() - ctx.openedAtMs : 0;
}

type AlertDialogRootProps = React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Root
> & {
  /** Optional friendly name for logging, e.g. "Change Password" */
  logDialogName?: string;
  /** What triggered the dialog, e.g. "Header menu" – included in logs and dialog context */
  logTrigger?: string;
  logTag: LogTags;
  logCategory: LogCategories;
  logSubCategory: LogSubCategories;
  logType?: LogEventTypeEnum;
};

const AlertDialog = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Root>,
  AlertDialogRootProps
>(
  (
    {
      logDialogName,
      logTrigger,
      logTag,
      logCategory,
      logSubCategory,
      logType = LogEventTypeEnum.PAGE_VIEW,
      onOpenChange,
      open,
      defaultOpen,
      ...props
    },
    _ref,
  ) => {
    const { logAction } = useActionLoggerOptional();
    const logActionRef = React.useRef(logAction);
    logActionRef.current = logAction;
    const lastOpenRef = React.useRef<boolean | undefined>(undefined);
    /** Prevents useEffect cleanup from logging close when handleOpenChange already did. */
    const closeAlreadyHandledRef = React.useRef(false);
    /** For uncontrolled + defaultOpen=true: prevents double-pop on unmount. */
    const uncontrolledContextPoppedRef = React.useRef(false);

    const closeWithLog = React.useCallback(() => {
      if (!logDialogName) return;
      const duration = getDialogOpenedDurationMs(logDialogName);
      if (duration < 100) return;
      logActionRef.current(
        normalizeTitle(`${logDialogName} dialog closed`),
        {},
        {
          // debounceMs > 0 makes suppressDuplicate actually work.
          debounceMs: 100,
          suppressDuplicate: true,
          category: logCategory,
          useTitleAsIs: true,
          type: logType,
          duration: getDialogOpenedDurationMs(logDialogName),
        },
      );
    }, [logDialogName, logCategory, logType]);
    const closeWithLogRef = React.useRef(closeWithLog);
    closeWithLogRef.current = closeWithLog;

    // Controlled mode: log open when open=true, log close in cleanup when open→false
    // unless handleOpenChange already fired the close log.
    React.useEffect(() => {
      if (!logDialogName || open === undefined) return;
      if (open) {
        closeAlreadyHandledRef.current = false;
        loggerContextManager.pushDialogContext({
          dialogName: logDialogName,
          openedFrom: logTrigger,
          tag: logTag ?? LogTags.NONE,
          category: logCategory ?? LogCategories.NONE,
          subCategory: logSubCategory ?? LogSubCategories.NONE,
          type: logType,
          openedAtMs: Date.now(),
        });
        logActionRef.current(
          normalizeTitle(`${logDialogName} dialog opened`),
          {},
          {
            debounceMs: 100,
            suppressDuplicate: true,
            useTitleAsIs: true,
            type: logType,
          },
        );
        return () => {
          if (!closeAlreadyHandledRef.current) {
            closeWithLogRef.current();
          }
          closeAlreadyHandledRef.current = false;
          loggerContextManager.popDialogContext();
        };
      }
    }, [
      open,
      logDialogName,
      logTrigger,
      logTag,
      logCategory,
      logSubCategory,
      logType,
    ]);

    // Uncontrolled mode with defaultOpen=true: Radix never calls onOpenChange(true) for
    // defaultOpen, so push context and log "opened" once on mount.
    React.useEffect(() => {
      if (!logDialogName || open !== undefined || !defaultOpen) return;
      uncontrolledContextPoppedRef.current = false;
      loggerContextManager.pushDialogContext({
        dialogName: logDialogName,
        openedFrom: logTrigger,
        tag: logTag ?? LogTags.NONE,
        category: logCategory ?? LogCategories.NONE,
        subCategory: logSubCategory ?? LogSubCategories.NONE,
        type: logType,
        openedAtMs: Date.now(),
      });
      logActionRef.current(
        normalizeTitle(`${logDialogName} dialog opened`),
        {},
        {
          debounceMs: 100,
          suppressDuplicate: true,
          useTitleAsIs: true,
          type: logType,
        },
      );
      return () => {
        if (!uncontrolledContextPoppedRef.current) {
          loggerContextManager.popDialogContext();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenChange = (nextOpen: boolean) => {
      // Push context for uncontrolled opens via trigger (never fires for defaultOpen).
      if (logDialogName && open === undefined && nextOpen) {
        loggerContextManager.pushDialogContext({
          dialogName: logDialogName,
          openedFrom: logTrigger,
          openedAtMs: Date.now(),
        });
      }

      if (logDialogName && lastOpenRef.current !== nextOpen) {
        lastOpenRef.current = nextOpen;

        if (!nextOpen) {
          // Log close and pop context BEFORE calling onOpenChange so the close log fires
          // even if onOpenChange causes an immediate unmount (e.g. dispatching Redux dequeue).
          closeWithLog();
          closeAlreadyHandledRef.current = true;
          if (open === undefined) {
            uncontrolledContextPoppedRef.current = true;
            loggerContextManager.popDialogContext();
          }
        } else {
          // Open via user interaction (uncontrolled, non-defaultOpen trigger).
          logActionRef.current(
            normalizeTitle(`${logDialogName} dialog opened`),
            {
              dialogName: logDialogName,
              openedFrom: logTrigger,
              open: nextOpen,
            },
            {
              debounceMs: 100,
              suppressDuplicate: true,
              category: logCategory,
              useTitleAsIs: true,
              type: logType,
            },
          );
        }
      }

      // Call onOpenChange LAST so all logging completes before any side-effect that
      // could unmount this component (e.g. dispatching dequeueAppAlertDialog).
      onOpenChange?.(nextOpen);
    };

    return (
      <AlertDialogPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    );
  },
);
AlertDialog.displayName = AlertDialogPrimitive.Root.displayName;

// Trigger remains a thin wrapper; logging is handled by Root open/close.
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/75 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-sm border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className,
      )}
      {...props}
    >
      {children}
      {/* <AlertDialogPrimitive.Cancel className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <span className="sr-only">Close</span>
            </AlertDialogPrimitive.Cancel> */}
    </AlertDialogPrimitive.Content>
  </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

type AlertDialogActionProps = React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Action
> & {
  /** Optional name to associate this confirm with a dialog */
  logDialogName?: string;
  /** Optional explicit action label (defaults from children text) */
  logActionLabel?: string;
  logCategory?: LogCategories;
};

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  AlertDialogActionProps
>(
  (
    { logDialogName, logActionLabel, logCategory, onClick, children, ...props },
    ref,
  ) => {
    const { logAction } = useActionLoggerOptional();

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (logDialogName) {
        const label = (
          logActionLabel ||
          getNodeText(children) ||
          "Confirm"
        ).trim();
        const title = buildButtonActionTitle(label, "CONFIRM", logDialogName);

        logAction(
          title,
          {
            dialogName: logDialogName,
            action: label,
            actionType: "confirm",
          },
          {
            debounceMs: 300,
            suppressDuplicate: true,
            category: logCategory,
          },
        );
      }

      onClick?.(e);
    };

    return (
      <AlertDialogPrimitive.Action ref={ref} onClick={handleClick} {...props}>
        {children}
      </AlertDialogPrimitive.Action>
    );
  },
);
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

type AlertDialogCancelProps = React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Cancel
> & {
  logDialogName?: string;
  logActionLabel?: string;
  logCategory?: LogCategories;
};

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  AlertDialogCancelProps
>(
  (
    { logDialogName, logActionLabel, logCategory, onClick, children, ...props },
    ref,
  ) => {
    const { logAction } = useActionLoggerOptional();

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (logDialogName) {
        const label = (
          logActionLabel ||
          getNodeText(children) ||
          "Cancel"
        ).trim();
        const title = buildButtonActionTitle(label, "CANCEL", logDialogName);

        logAction(
          title,
          {
            dialogName: logDialogName,
            action: label,
            actionType: "cancel",
          },
          {
            debounceMs: 300,
            suppressDuplicate: true,
            category: logCategory,
          },
        );
      }

      onClick?.(e);
    };

    return (
      <AlertDialogPrimitive.Cancel ref={ref} onClick={handleClick} {...props}>
        {children}
      </AlertDialogPrimitive.Cancel>
    );
  },
);
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogPortal,
  AlertDialogOverlay,
};
